import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import {
  ACTIVATION_PAYMENT_METHOD,
  ActivationPaymentMethodCode,
  ActivationPaymentPayload,
  IActivationPaymentFormValues,
  IActivationPaymentMethod,
  IPaymentMethodDto,
  SUPPORTED_PAYMENT_METHOD_CODES
} from "../types";
import { isValidIBANNumber } from "./activationPortalHelpers";

/**
 * Which payment methods are offered, and what each one collects.
 *
 * Kept apart from `activationPortalHelpers` — already past a thousand lines —
 * so the routing rule stays testable on its own.
 */

// ─────────────────────────── IBAN country ───────────────────────────

/**
 * The IBAN's country code, or `null` while the value is not yet a structurally
 * valid IBAN.
 *
 * Returning `null` for incomplete input is deliberate: a half-typed IBAN would
 * otherwise report whatever country its first two characters happen to spell,
 * and the offered methods would flicker as the customer types.
 */
export const getIbanCountryCode = (iban?: string): string | null => {
  if (!iban) return null;

  const normalised = String(iban)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

  if (!isValidIBANNumber(normalised)) return null;

  return normalised.slice(0, 2);
};

// ─────────────────────────── Catalog narrowing ───────────────────────────

/** Codes already reported this session, so Sentry gets one event per code. */
const reportedUnknownCodes = new Set<string>();

/**
 * Narrows an API catalog to the methods this client can render.
 *
 * The catalog is data-driven, but each method still needs a component that
 * knows which fields it collects, so a code we do not implement is dropped
 * rather than rendered blank. Dropping is silent to the customer and reported
 * once to Sentry, because it means the backend is ahead of the front end and
 * someone should know.
 */
export const toSupportedPaymentMethods = (
  methods: IPaymentMethodDto[]
): IActivationPaymentMethod[] => {
  const supported: IActivationPaymentMethod[] = [];

  methods.forEach((method) => {
    if (SUPPORTED_PAYMENT_METHOD_CODES.indexOf(method.code) !== -1) {
      supported.push(method as IActivationPaymentMethod);
      return;
    }

    if (!reportedUnknownCodes.has(method.code)) {
      reportedUnknownCodes.add(method.code);
      Sentry.captureMessage(
        `Activation portal: unsupported payment method code "${method.code}" in catalog`,
        "warning"
      );
    }
  });

  return supported;
};

/** Exposed for tests; the Set is module state and would leak between cases. */
export const resetReportedUnknownCodes = (): void => {
  reportedUnknownCodes.clear();
};

// ─────────────────────────── Eligibility ───────────────────────────

const bySortOrder = (
  a: IActivationPaymentMethod,
  b: IActivationPaymentMethod
): number => {
  // Methods without a sortOrder sink below those that have one.
  const aOrder =
    a.sortOrder === undefined ? Number.MAX_SAFE_INTEGER : a.sortOrder;
  const bOrder =
    b.sortOrder === undefined ? Number.MAX_SAFE_INTEGER : b.sortOrder;

  if (aOrder !== bOrder) return aOrder - bOrder;
  return a.displayName.localeCompare(b.displayName);
};

/**
 * The methods offered for a given IBAN country.
 *
 * `supportedIbanCountries: null` means no restriction; an empty array means no
 * country qualifies and is not treated as a synonym. Exclusions are applied
 * after inclusions — that is how the fallback is expressed: available
 * everywhere, withheld wherever the primary method already covers the customer.
 *
 * While the country is unknown, only a method that depends on it in no way at
 * all qualifies; anything country-dependent waits until we know.
 */
export const getEligiblePaymentMethods = (
  catalog: IActivationPaymentMethod[],
  ibanCountryCode: string | null
): IActivationPaymentMethod[] =>
  catalog
    .filter((method) => method.isEnabled)
    .filter((method) => {
      const { supportedIbanCountries, excludedIbanCountries } = method;
      const hasExclusions = !!excludedIbanCountries?.length;

      if (ibanCountryCode === null) {
        return supportedIbanCountries === null && !hasExclusions;
      }

      if (
        supportedIbanCountries !== null &&
        supportedIbanCountries.indexOf(ibanCountryCode) === -1
      ) {
        return false;
      }

      return !(
        hasExclusions && excludedIbanCountries!.indexOf(ibanCountryCode) !== -1
      );
    })
    .sort(bySortOrder);

export const findPaymentMethod = (
  catalog: IActivationPaymentMethod[],
  code: ActivationPaymentMethodCode | ""
): IActivationPaymentMethod | null => {
  if (!code) return null;
  return catalog.find((method) => method.code === code) || null;
};

// ─────────────────────────── Payload mapping ───────────────────────────

/**
 * Builds the wire payload from flat form state.
 *
 * The only place that knows which fields belong to which method. Methods that
 * do not use mandate consent omit those flags entirely rather than sending
 * `false`, so the API is never invited to branch on a field that does not apply.
 */
export const toActivationPaymentPayload = (
  values: IActivationPaymentFormValues
): ActivationPaymentPayload => {
  const base = {
    accountHolderName: values.accountHolderName,
    bankAccountNumber: values.bankAccountNumber,
    termsAndConditions: values.termsAndConditions,
    privacyPolicy: values.privacyPolicy
  };

  switch (values.paymentMethod) {
    case ACTIVATION_PAYMENT_METHOD.AUTOMATIC_COLLECTION:
      return {
        ...base,
        paymentMethod: ACTIVATION_PAYMENT_METHOD.AUTOMATIC_COLLECTION,
        authorizeAllsetra: values.authorizeAllsetra,
        directDebit: values.directDebit
      };

    case ACTIVATION_PAYMENT_METHOD.MANUAL_BANK_TRANSFER:
      return {
        ...base,
        paymentMethod: ACTIVATION_PAYMENT_METHOD.MANUAL_BANK_TRANSFER
      };

    case ACTIVATION_PAYMENT_METHOD.DIRECT_PAYMENT:
      return {
        ...base,
        paymentMethod: ACTIVATION_PAYMENT_METHOD.DIRECT_PAYMENT
      };

    default:
      throw new Error(
        "Cannot build an activation payment payload without a selected method"
      );
  }
};

// ─────────────────────────── Initial values ───────────────────────────

export const paymentMethodInitialValues: IActivationPaymentFormValues = {
  paymentMethod: "",
  accountHolderName: "",
  bankAccountNumber: "",
  authorizeAllsetra: false,
  directDebit: false,
  termsAndConditions: false,
  privacyPolicy: false
};

// ─────────────────────────── Validation ───────────────────────────

const t = (translator: any, key: string): string =>
  translator(`validationMessage.${key}`, { ns: "activationPortal" });

/**
 * Validation for the payment step, varying by the selected method.
 *
 * Deliberately absent: any check on the IBAN's country. Country decides which
 * methods are offered, not whether the IBAN is acceptable — a French IBAN is a
 * valid input that happens to route somewhere else.
 *
 * The client's rules exist for feedback. The API revalidates on submit.
 */
export const paymentMethodValidationSchema = (
  translator: any,
  selectedMethod: IActivationPaymentMethod | null
) => {
  const requiresIban = selectedMethod?.requiresIban ?? false;
  const requiresMandate = selectedMethod?.requiresMandateConsent ?? false;

  const mandateConsent = (labelKey: string, requiredKey: string) => {
    const field = Yup.bool().label(t(translator, labelKey));
    return requiresMandate
      ? field.oneOf([true], t(translator, requiredKey))
      : field;
  };

  return Yup.object({
    // Nothing is pre-selected on the customer's behalf; the step cannot advance
    // until they choose.
    paymentMethod: Yup.string()
      .trim()
      .required(t(translator, "paymentMethodRequired"))
      .label(t(translator, "paymentMethod")),

    accountHolderName: requiresIban
      ? Yup.string()
          .trim()
          .required(t(translator, "accountHolderNameRequired"))
          .label(t(translator, "accountHolderName"))
      : Yup.string().trim().label(t(translator, "accountHolderName")),

    bankAccountNumber: requiresIban
      ? Yup.string()
          .trim()
          .required(t(translator, "bankAccountNumberRequired"))
          // @ts-ignore — `iban` is registered via Yup.addMethod in
          // activationPortalHelpers, which this module already imports.
          .iban(t(translator, "bankAccountNumber"))
          .label(t(translator, "bankAccountNumber"))
      : Yup.string().trim().label(t(translator, "bankAccountNumber")),

    authorizeAllsetra: mandateConsent(
      "authorizeAllsetra",
      "authorizeAllsetraRequired"
    ),
    directDebit: mandateConsent("directDebit", "directDebitRequired"),

    // Terms and privacy apply to every method.
    termsAndConditions: Yup.bool()
      .label(t(translator, "termsAndConditions"))
      .oneOf([true], t(translator, "termsAndConditionsRequired")),
    privacyPolicy: Yup.bool()
      .label(t(translator, "privacyPolicy"))
      .oneOf([true], t(translator, "privacyPolicyRequired"))
  });
};
